// src/pages/ScanWholesalePage.tsx
import React, { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { DecodeHintType, BarcodeFormat, Result } from "@zxing/library";
import { apiUrl } from "../config/api";

// ---------- Types ----------
type Product = {
  code: string;
  name: string;
  wholesalePrice: number;
  unit?: string;
};

type ItemEntry = {
  product: Product;
  qty: number;
  customPrice?: number;
};

// ประเภทการชำระเงิน
type PaymentMethod = "เงินสด" | "สแกน" | "Alipay" | "WeChat" | "บัตรเดบิต";

/** ==================== Autofocus & Camera helpers ==================== */
/** เปิด continuous autofocus (ถ้าอุปกรณ์รองรับ) */
async function enableContinuousAutofocus(track: MediaStreamTrack) {
  const caps: any = track.getCapabilities?.() || {};
  const modes: string[] = caps.focusMode || [];
  if (modes.includes("continuous")) {
    await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] } as any);
  } else if (modes.includes("auto")) {
    await track.applyConstraints({ advanced: [{ focusMode: "auto" }] } as any);
  }
}

/** สั่งโฟกัสใหม่ 1 ครั้ง แล้วกลับสู่ continuous/auto */
async function refocusOnce(track: MediaStreamTrack) {
  const caps: any = track.getCapabilities?.() || {};
  const modes: string[] = caps.focusMode || [];
  if (modes.includes("single-shot")) {
    await track.applyConstraints({ advanced: [{ focusMode: "single-shot" }] } as any);
    if (modes.includes("continuous")) {
      await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] } as any);
    } else if (modes.includes("auto")) {
      await track.applyConstraints({ advanced: [{ focusMode: "auto" }] } as any);
    }
  } else {
    // เครื่องไม่รองรับ: toggle ความละเอียดเล็กน้อยเพื่อบังคับโฟกัสใหม่
    const s = track.getSettings?.();
    const w = s?.width || 1280;
    await track.applyConstraints({ width: { ideal: w - 1 } } as any);
    await track.applyConstraints({ width: { ideal: w } } as any);
  }
}

/** แตะจอเพื่อโฟกัสตำแหน่ง (รองรับบางเบราว์เซอร์เท่านั้น) */
async function tapToFocus(
  track: MediaStreamTrack,
  videoEl: HTMLVideoElement,
  clientX: number,
  clientY: number
) {
  const rect = videoEl.getBoundingClientRect();
  const x = (clientX - rect.left) / rect.width;
  const y = (clientY - rect.top) / rect.height;
  try {
    await track.applyConstraints({
      advanced: [{ pointsOfInterest: [{ x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) }] }],
    } as any);
    await refocusOnce(track);
  } catch {
    // ไม่รองรับก็เงียบ ๆ
  }
}

/** ซูม (ถ้ากล้องรองรับ) */
async function trySetZoom(track: MediaStreamTrack, z: number) {
  const caps: any = track.getCapabilities?.() || {};
  if (caps.zoom) {
    const v = Math.max(caps.zoom.min, Math.min(caps.zoom.max, z));
    await track.applyConstraints({ advanced: [{ zoom: v }] } as any);
  }
}

/** ปรับชดเชยแสงทีละนิด (ถ้ากล้องรองรับ) */
async function bumpExposure(track: MediaStreamTrack, step = 0.3) {
  const caps: any = track.getCapabilities?.() || {};
  if (typeof caps.exposureCompensation?.min === "number") {
    const cur: any = track.getSettings?.() || {};
    const next = Math.max(
      caps.exposureCompensation.min,
      Math.min(caps.exposureCompensation.max, (cur.exposureCompensation ?? 0) + step)
    );
    await track.applyConstraints({ advanced: [{ exposureCompensation: next }] } as any);
  }
}

/** รอให้ <video> พร้อมก่อน (มีขนาดวิดีโอจริง) */
async function waitVideoReady(video: HTMLVideoElement): Promise<void> {
  if (video.readyState >= 2 && video.videoWidth > 0) return;
  await new Promise<void>((resolve) => {
    const cb = () => {
      if (video.videoWidth > 0) {
        video.removeEventListener("loadeddata", cb);
        resolve();
      }
    };
    video.addEventListener("loadeddata", cb);
  });
}

/** =========================================================== */

export default function ScanPage(): JSX.Element {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // 🔧 work canvas สำหรับ ROI & scale ladder
  const workCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const ensureCanvas = () => {
    if (!workCanvasRef.current) workCanvasRef.current = document.createElement("canvas");
    return workCanvasRef.current!;
  };

  // loop ควบคุมแอนิเมชันขณะ TRY_HARDER
  const scanLoopRef = useRef<number | null>(null);
  const gotOneRef = useRef<boolean>(false);

  // ---------- ZXing Readers (Fast + Hard) ----------
  const [readerFast] = useState(() => {
    const r = new BrowserMultiFormatReader();
    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.QR_CODE,
      BarcodeFormat.EAN_13,
      BarcodeFormat.EAN_8,
      BarcodeFormat.CODE_128,
    ]);
    r.setHints?.(hints);
    return r;
  });

  const [readerHard] = useState(() => {
    const r = new BrowserMultiFormatReader();
    const hints = new Map();
    hints.set(DecodeHintType.TRY_HARDER, true);
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.QR_CODE,
      BarcodeFormat.EAN_13,
      BarcodeFormat.EAN_8,
      BarcodeFormat.CODE_128,
      BarcodeFormat.CODE_39,
      BarcodeFormat.ITF,
      BarcodeFormat.UPC_A,
      BarcodeFormat.UPC_E,
      BarcodeFormat.DATA_MATRIX,
      BarcodeFormat.PDF_417,
      BarcodeFormat.AZTEC,
    ]);
    r.setHints?.(hints);
    return r;
  });

  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState<string | undefined>(undefined);

  const [resultText, setResultText] = useState<string>("");
  const [errorMsg, setErrorMsg] = useState<string>("");

  const [saving, setSaving] = useState(false);

  // เลขบิลรันนิ่ง (เริ่ม 000)
  const [billSeq, setBillSeq] = useState(0);

  // ✅ หลายรายการ พร้อมจำนวน
  const [items, setItems] = useState<ItemEntry[]>([]);
  const [editingPriceIdx, setEditingPriceIdx] = useState<number | null>(null);
  const [draftPrice, setDraftPrice] = useState<string>("");

  const getEffectivePrice = (entry: ItemEntry) => entry.customPrice ?? entry.product.wholesalePrice;

  const confirmPrice = (idx: number) => {
    const v = parseFloat(draftPrice);
    if (!isNaN(v) && v >= 0) {
      setItems((prev) => {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], customPrice: v };
        return copy;
      });
    }
    setEditingPriceIdx(null);
  };

  // รวมยอดทั้งหมด
  const grandTotal = items.reduce((sum, it) => sum + getEffectivePrice(it) * it.qty, 0);
  const totalQty = items.reduce((sum, it) => sum + it.qty, 0);

  const [loadingProduct, setLoadingProduct] = useState<boolean>(false);
  const [showManual, setShowManual] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [toast, setToast] = useState<{ show: boolean; message: string }>({ show: false, message: "" });

  // ตัวเลือกไว้ทำปุ่มการ์ด
  const PAYMENT_OPTIONS: PaymentMethod[] = ["เงินสด", "สแกน", "Alipay", "WeChat", "บัตรเดบิต"];

  // เก็บวิธีชำระเงิน และสถานะ popup
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(true);

  // แคชสินค้า
  const [productsByCode, setProductsByCode] = useState<Map<string, Product>>(new Map());
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productsError, setProductsError] = useState<string | null>(null);

  const [discount, setDiscount] = useState<number>(0); // บาท
  const netTotal = Math.max(0, grandTotal - (discount || 0));

  // modal ส่วนลด
  const [showDiscountPanel, setShowDiscountPanel] = useState(false);
  const [draftDiscount, setDraftDiscount] = useState<number>(0);
  const [discountErr, setDiscountErr] = useState<string>("");

  // เลือกไฟล์ภาพ (ถ่ายจากกล้อง/อัปโหลด)
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [imgPreview, setImgPreview] = useState<string | null>(null);

  // ---------- เสียงติ๊ดตอนสแกนสำเร็จ ----------
  const playBeep = () => {
    try {
      const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
      const ctx = new AudioCtx();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = 880;
      o.connect(g);
      g.connect(ctx.destination);
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.01);
      o.start();
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.15);
      o.stop(ctx.currentTime + 0.16);
    } catch {}
  };

  // โหลดรูปเป็น bitmap/image
  async function loadImageBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
    // ใช้ createImageBitmap ถ้ามี (เร็วกว่า)
    if ("createImageBitmap" in window) {
      return await createImageBitmap(file);
    }
    // fallback
    const url = URL.createObjectURL(file);
    try {
      const img = new Image();
      img.decoding = "async";
      img.src = url;
      await new Promise<void>((res, rej) => {
        img.onload = () => res();
        img.onerror = () => rej(new Error("โหลดรูปไม่สำเร็จ"));
      });
      return img;
    } finally {
      // ปล่อย URL ไว้จนกว่าจะวาดเสร็จ
    }
  }

  async function decodeFromImageFile(file: File) {
    setErrorMsg("");
    setResultText("");
    setImgPreview(URL.createObjectURL(file)); // แสดงพรีวิวเล็ก ๆ

    try {
      const bitmap = await loadImageBitmap(file);

      // ย่อภาพลงก่อน (กันภาพมือถือ 4K หนักเกิน)
      const maxSide = 1600; // ปรับได้
      const iw = "width" in bitmap ? (bitmap as any).width : (bitmap as HTMLImageElement).naturalWidth;
      const ih = "height" in bitmap ? (bitmap as any).height : (bitmap as HTMLImageElement).naturalHeight;
      const scale = Math.min(1, maxSide / Math.max(iw, ih));
      const cw = Math.max(1, Math.floor(iw * scale));
      const ch = Math.max(1, Math.floor(ih * scale));

      const canvas = ensureCanvas();
      canvas.width = cw;
      canvas.height = ch;
      const ctx = canvas.getContext("2d")!;
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, cw, ch);
      ctx.drawImage(bitmap as any, 0, 0, cw, ch);

      // ลองถอดรหัสหลายมุม (กัน EXIF/orientation)
      const tryAngles = [0, 90, 180, 270];
      let decoded: string | null = null;

      for (const ang of tryAngles) {
        if (ang !== 0) {
          // หมุนภาพบนแคนวาส
          const tmp = ensureCanvas();
          const w = (ang % 180 === 0) ? cw : ch;
          const h = (ang % 180 === 0) ? ch : cw;
          tmp.width = w; tmp.height = h;
          const c2 = tmp.getContext("2d")!;
          c2.fillStyle = "#fff";
          c2.fillRect(0, 0, w, h);
          c2.save();
          c2.translate(w / 2, h / 2);
          c2.rotate((ang * Math.PI) / 180);
          c2.drawImage(canvas, -cw / 2, -ch / 2);
          c2.restore();

          try {
            const res = await readerHard.decodeFromCanvas(tmp);
            decoded = res?.getText?.()?.trim() || null;
            if (decoded) break;
          } catch { /* try next angle */ }
        } else {
          try {
            const res = await readerHard.decodeFromCanvas(canvas);
            decoded = res?.getText?.()?.trim() || null;
            if (decoded) break;
          } catch { /* rotate next */ }
        }
      }

      if (!decoded) {
        setErrorMsg("อ่าน QR จากรูปไม่สำเร็จ — ลองถ่ายให้ชัด/สว่างขึ้น หรือครอปให้ใกล้ QR");
        return;
      }
      await handleDecodedCode(decoded);
    } catch (e: any) {
      setErrorMsg(e?.message ?? "ประมวลผลไฟล์ภาพไม่สำเร็จ");
    }
  }

  // ---------- โหลดรายชื่อกล้อง ----------
  useEffect(() => {
    (async () => {
      try {
        const list = await BrowserMultiFormatReader.listVideoInputDevices();
        setDevices(list);
        // ดีฟอลต์ใช้กล้องหลังถ้ามี (ส่วนใหญ่ label มีคำว่า back / environment)
        const back = list.find((d) => /back|environment/i.test(d.label || ""));
        if (!deviceId && list.length) setDeviceId((back ?? list[list.length - 1]).deviceId);
      } catch (e: any) {
        setErrorMsg(e?.message ?? "ไม่สามารถอ่านรายชื่อกล้องได้");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // auto-hide toast
  useEffect(() => {
    if (!toast.show) return;
    const t = setTimeout(() => setToast({ show: false, message: "" }), 2000);
    return () => clearTimeout(t);
  }, [toast.show]);

  // ---------- โหลดสินค้าตอนเปิดหน้า ----------
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoadingProducts(true);
        setProductsError(null);

        const resp = await fetch(apiUrl("/products"));
        const json = await resp.json();
        if (!resp.ok) throw new Error(json?.error ?? resp.statusText);

        const list: Product[] = (json.items ?? []).map((r: any) => ({
          code: r.code,
          name: r.name,
          wholesalePrice: Number(
            r.wholesalePrice ?? r.price ?? 0 // กันกรณี API รุ่นเก่ามีแต่ price
          ),
          unit: r.unit ?? undefined,
        }));

        if (!alive) return;
        const map = new Map<string, Product>();
        for (const p of list) {
          map.set(p.code, p);
          map.set(p.code.toLowerCase(), p);
        }
        setProductsByCode(map);
      } catch (e: any) {
        if (!alive) return;
        setProductsError(e?.message ?? "ดึงรายการสินค้าไม่สำเร็จ");
      } finally {
        if (alive) setLoadingProducts(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // PMN-CRITICAL(/ScanWholesalePage): wholesale bills must use wholesalePrice from product master/cache, not retail price.
  const fetchProduct = async (code: string): Promise<Product | null> => {
    setLoadingProduct(true);
    setErrorMsg("");
    try {
      const key = code.trim();
      const cached = productsByCode.get(key) ?? productsByCode.get(key.toLowerCase());
      if (cached) {
    // กันกรณี state เดิมถูก map เป็น { price } มาก่อน
        const hasWs = typeof (cached as any).wholesalePrice === "number";
        const fallback = Number((cached as any).price ?? 0);
        return hasWs ? cached : { ...cached, wholesalePrice: fallback };
      }

      const url = new URL(apiUrl("/products"));
      url.searchParams.set("q", code);
      const res = await fetch(url.toString(), { headers: { Accept: "application/json" } });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) throw new Error(data?.error ?? res.statusText);

      const items = Array.isArray(data?.items) ? data.items : [];
      const found =
        items.find((it: any) => String(it.code).toLowerCase() === key.toLowerCase()) ?? items[0];
      if (!found) return null;

      const p: Product = {
        code: found.code,
        name: found.name,
        wholesalePrice: Number(found.wholesalePrice ?? 0),
        unit: found.unit ?? undefined,
      };

      setProductsByCode((prev) => {
        const next = new Map(prev);
        next.set(p.code, p);
        next.set(p.code.toLowerCase(), p);
        return next;
      });

      return p;
    } catch (e: any) {
      setErrorMsg(e?.message ?? "ดึงข้อมูลสินค้าไม่สำเร็จ");
      return null;
    } finally {
      setLoadingProduct(false);
    }
  };

  function resetUIAfterSave(bill: string) {
    setItems([]);
    setResultText("");
    setErrorMsg("");
    setDiscount(0);
    setShowManual(false);
    setScanning(false);
    setToast({ show: true, message: `บันทึกบิล ${bill} เรียบร้อย` });

    stopScanLoop();
    stopStream();

    setBillSeq(Number(bill) + 1);
  }

  // ---------- กล้อง: ควบคุม loop & stream ----------
  const stopScanLoop = () => {
    if (scanLoopRef.current) cancelAnimationFrame(scanLoopRef.current);
    scanLoopRef.current = null;
    gotOneRef.current = false;
  };

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  // ---------- วาด ROI ลง canvas ----------
  const drawROIToCanvas = (video: HTMLVideoElement, scale = 1) => {
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) return null;
    const roiPct = { w: 0.8, h: 0.8 };
    const rw = vw * roiPct.w;
    const rh = vh * roiPct.h;
    const rx = (vw - rw) / 2;
    const ry = (vh - rh) / 2;
    const canvas = ensureCanvas();
    canvas.width = Math.max(1, Math.floor(rw * scale));
    canvas.height = Math.max(1, Math.floor(rh * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(video, rx, ry, rw, rh, 0, 0, canvas.width, canvas.height);
    return canvas;
  };

  // ---------- เฟสเร็ว (decode จาก <video> ตรง ๆ) ----------
  async function fastPhase(video: HTMLVideoElement): Promise<string | null> {
    return new Promise<string | null>((resolve) => {
      let gotOne = false;
  
      // คืนค่าเป็น Promise<IScannerControls>
      const controlsPromise = readerFast.decodeFromVideoElement(
        video,
        (result: Result | undefined /* or null */, _err?: unknown) => {
          if (gotOne) return;
          if (result) {
            gotOne = true;
            // ต้อง stop ผ่าน then ของพรอมิส
            controlsPromise.then((c) => c.stop()).catch(() => {});
            resolve(result.getText().trim());
          }
        }
      );
  
      // timebox เฟสเร็ว ~800ms
      setTimeout(() => {
        if (!gotOne) {
          controlsPromise.then((c) => c.stop()).catch(() => {});
          resolve(null);
        }
      }, 800);
    });
  }
  

  // ---------- จัดการเมื่อได้โค้ด ----------
  const handleDecodedCode = async (code: string) => {
    // PMN-CRITICAL(/ScanWholesalePage): all decoded paths merge qty here and must stop the camera after one accepted code to avoid duplicate sale items.
    if (gotOneRef.current) return;
    gotOneRef.current = true;

    const nowCode = code.trim();
    setResultText(nowCode);
    playBeep();
    let toastName = "";
    let toastQty = 1;

    const p = await fetchProduct(nowCode);
    if (!p) {
      setErrorMsg(`ไม่พบสินค้า code: ${nowCode}`);
      setScanning(false);
      stopScanLoop();
      stopStream();
      return;
    }

    setItems((prev) => {
      const idx = prev.findIndex((it) => it.product.code === nowCode);
      if (idx >= 0) {
        const copy = [...prev];
        const newQty = copy[idx].qty + 1;
        copy[idx] = { ...copy[idx], qty: newQty };
        toastName = copy[idx].product.name;
        toastQty = newQty;
        return copy;
      }
      toastName = p.name;
      toastQty = 1;
      return [{ product: p, qty: 1 }, ...prev];
    });
    setToast({ show: true, message: `เพิ่ม ${toastName} x${toastQty}` });

    // หยุดทันทีหลังสแกน 1 ครั้ง
    setScanning(false);
    stopScanLoop();
    stopStream();
  };

  // ---------- วน TRY_HARDER ด้วย ROI + scale ladder + zoom/exposure ----------
  const startScanLoop = () => {
    // PMN-CRITICAL(/ScanWholesalePage): hard scan loop owns ROI/scale ladder and must respect scanning + gotOneRef for cleanup and duplicate prevention.
    const video = videoRef.current!;
    const track = streamRef.current?.getVideoTracks?.()[0];
    const hardScales = [1, 1.5, 2];
    let zoom = 1;
    let lastAdj = performance.now();

    const hardStep = async () => {
      if (!scanning || gotOneRef.current) return;

      // ลองหลายสเกลบน ROI
      let decoded: string | null = null;
      for (const s of hardScales) {
        const canvas = drawROIToCanvas(video, s);
        if (!canvas) continue;
        try {
          const res = await readerHard.decodeFromCanvas(canvas);
          const txt = res?.getText?.();
          if (txt) {
            decoded = txt.trim();
            break;
          }
        } catch {}
      }

      if (decoded) {
        await handleDecodedCode(decoded);
        return;
      }

      // ค่อย ๆ เพิ่มความสว่าง/ซูม เพื่อช่วยอ่าน
      const now = performance.now();
      if (track && now - lastAdj > 700) {
        lastAdj = now;
        await bumpExposure(track, 0.3).catch(() => {});
        zoom = Math.min(zoom + 0.2, 2.0);
        await trySetZoom(track, zoom).catch(() => {});
      }
      scanLoopRef.current = requestAnimationFrame(hardStep);
    };

    (async () => {
      // เฟสเร็ว
      const quick = await fastPhase(video);
      if (quick) {
        await handleDecodedCode(quick);
        return;
      }
      // เฟสยาก
      scanLoopRef.current = requestAnimationFrame(hardStep);
    })();
  };

  // ---------- เริ่ม/หยุดกล้อง + วงจรสแกน ----------
  useEffect(() => {
    if (!videoRef.current) return;

    if (!scanning) {
      stopScanLoop();
      stopStream();
      return;
    }

    (async () => {
      try {
        setErrorMsg("");
        setResultText("");
        gotOneRef.current = false;

        const constraints: MediaStreamConstraints = {
          video: deviceId
            ? { deviceId: { exact: deviceId }, width: { ideal: 1280, min: 640 }, height: { ideal: 720, min: 480 } }
            : { facingMode: { ideal: "environment" }, width: { ideal: 1280, min: 640 }, height: { ideal: 720, min: 480 } },
          audio: false,
        };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        videoRef.current!.srcObject = stream;
        streamRef.current = stream;
        await videoRef.current!.play();
        await waitVideoReady(videoRef.current!);

        const track = stream.getVideoTracks()[0];
        enableContinuousAutofocus(track).catch(() => {});

        startScanLoop();
      } catch (e: any) {
        setErrorMsg(e?.message ?? "เปิดกล้องไม่ได้ (ต้อง https หรือ localhost)");
        setScanning(false);
      }
    })();

    return () => {
      stopScanLoop();
      stopStream();
    };
  }, [deviceId, scanning]);

  // ---------- ไฟฉาย ----------
  const toggleTorch = async (on: boolean) => {
    try {
      const track = streamRef.current?.getVideoTracks?.()[0];
      if (!track) {
        setErrorMsg("ไม่พบวิดีโอแทร็ก");
        return;
      }
      const caps = (track.getCapabilities?.() as any) as { torch?: boolean };
      if (caps?.torch) {
        await track.applyConstraints({ advanced: [{ torch: on }] } as any);
      } else {
        setErrorMsg("อุปกรณ์นี้ไม่รองรับไฟฉาย");
      }
    } catch {
      setErrorMsg("เปิดไฟฉายไม่สำเร็จ");
    }
  };

  const stopScan = () => {
    setScanning(false);
    stopScanLoop();
    stopStream();
  };

  // ---------- เปลี่ยนจำนวนด้วย input ----------
  const setQty = (index: number, value: number) => {
    setItems((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], qty: Math.max(0, Math.floor(isNaN(value) ? 0 : value)) };
      return copy;
    });
  };
  const removeItem = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };
  const clearItems = () => setItems([]);

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center wholesale-page-shell">
      {loadingProducts && (
        <div className="d-flex align-items-center gap-2 mb-2">
          <div className="spinner-border spinner-border-sm" /> <span>กำลังโหลดรายการสินค้า…</span>
        </div>
      )}
      {productsError && (
        <div className="alert alert-warning rounded-3 mb-2">โหลดสินค้าไม่สำเร็จ: {productsError}</div>
      )}

      <div className="container py-4 py-md-5">
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 col-lg-6">
            <div className="card shadow rounded-4 wholesale-page-card">
              <div className="card-body p-4 p-md-5">
                {/* PMN-NOTE(/ScanWholesalePage): violet theme + ribbon prevents user from confusing this page with retail /scan (both look identical without visual cues). */}
                <div className="wholesale-ribbon">🏪 ราคาส่ง · WHOLESALE MODE</div>
                <h3 className="mb-3 text-center wholesale-header">สแกนบันทึกสินค้าขาย</h3>

                {/* Toast แจ้งเตือนมุมขวาบน */}
                <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1080 }}>
                  {toast.show && (
                    <div className="alert alert-success shadow mb-0 rounded-3 py-2 px-3">{toast.message}</div>
                  )}
                </div>

                {/* กล่องแสดงวิธีชำระเงินที่เลือก */}
                <div className="alert alert-secondary d-flex justify-content-between align-items-center rounded-3">
                  <div>
                    วิธีชำระเงิน: <strong>{paymentMethod ?? "ยังไม่เลือก"}</strong>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => setShowPaymentModal(true)}
                  >
                    เปลี่ยน
                  </button>
                </div>

                {/* Popup เลือกวิธีชำระเงิน */}
                {showPaymentModal && (
                  <div
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 2000 }}
                  >
                    <div className="card shadow-lg border-0 rounded-4" style={{ maxWidth: 720, width: "92%" }}>
                      <div className="card-body p-4">
                        <h5 className="mb-3 text-center">เลือกวิธีชำระเงิน</h5>
                        <div className="row row-cols-2 g-3">
                          {PAYMENT_OPTIONS.map((opt) => (
                            <div className="col" key={opt}>
                              <button
                                type="button"
                                className={`card w-100 h-100 border-2 rounded-4 text-decoration-none bg-secondary text-black ${
                                  paymentMethod === opt ? "border-primary" : "border-light"
                                }`}
                                onClick={() => {
                                  setPaymentMethod(opt);
                                  setShowPaymentModal(false);
                                }}
                              >
                                <div
                                  className="card-body d-flex align-items-center justify-content-center"
                                  style={{ minHeight: 100 }}
                                >
                                  <div className="text-center">
                                    <div className="fw-bold">{opt}</div>
                                    {paymentMethod === opt && (
                                      <div className="small text-primary mt-1">✓ เลือกแล้ว</div>
                                    )}
                                  </div>
                                </div>
                              </button>
                            </div>
                          ))}
                        </div>

                        <div className="text-center mt-3">
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => setShowPaymentModal(false)}
                          >
                            ข้ามไปก่อน
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* โซนกล้อง */}
                <div className="ratio ratio-4x3 bg-secondary rounded-3 mb-3 d-flex align-items-center justify-content-center">
                  <video
                    ref={videoRef}
                    className="w-100 h-100 rounded-3"
                    muted
                    playsInline
                    autoPlay
                    onClick={(e) => {
                      const track = streamRef.current?.getVideoTracks?.()[0];
                      if (!track || !videoRef.current) return;
                      tapToFocus(track, videoRef.current, e.clientX, e.clientY).catch(() => {});
                    }}
                  />
                </div>

                {/* เลือกกล้อง + ไฟ + โฟกัสใหม่ */}
                <div className="row gy-2 align-items-center mb-3">
                  <div className="col-12 col-md-6">
                    <select
                      className="form-select"
                      value={deviceId ?? ""}
                      onChange={(e) => setDeviceId(e.target.value)}
                    >
                      {devices.map((d, idx) => (
                        <option key={d.deviceId || idx} value={d.deviceId}>
                          {d.label || `Camera ${idx + 1}`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-6 d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary flex-fill"
                      onClick={() => toggleTorch(true)}
                    >
                      เปิดไฟ
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary flex-fill"
                      onClick={() => toggleTorch(false)}
                    >
                      ปิดไฟ
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-primary flex-fill"
                      onClick={async () => {
                        const track = streamRef.current?.getVideoTracks?.()[0];
                        if (!track) {
                          setErrorMsg("ยังไม่พบกล้อง/สตรีม");
                          return;
                        }
                        await refocusOnce(track).catch(() => {});
                      }}
                    >
                      โฟกัสใหม่
                    </button>
                  </div>
                </div>

                {/* ปุ่ม: สแกน 1 ครั้ง (ด้วยเฟสเร็ว แล้วตกชั้น TRY_HARDER อัตโนมัติ) */}
                <div className="d-grid gap-2 mb-3">
                  <button
                    type="button"
                    className="btn btn-success btn-lg"
                    disabled={scanning}
                    onClick={() => setScanning(true)}
                  >
                    {scanning ? "กำลังสแกน..." : "▶️ สแกน 1 ครั้ง"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={stopScan}
                  >
                    🛑 หยุดกล้อง
                  </button>
                </div>

                {/* สถานะ / โค้ดล่าสุด */}
                {resultText && (
                  <div className="alert alert-info rounded-3 py-2 mb-3" role="alert">
                    โค้ดล่าสุด: <span className="fw-semibold">{resultText}</span>
                  </div>
                )}
                {loadingProduct && (
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <div className="spinner-border spinner-border-sm" role="status" />
                    <span>กำลังดึงข้อมูลสินค้า…</span>
                  </div>
                )}

                {/* รายการสินค้า */}
                {items.length > 0 && (
                  <div className="mb-3">
                    {items.map((entry, idx) => (
                      <div
                        key={`${entry.product.code}-${idx}`}
                        className="card border rounded-3 shadow-sm mb-2"
                        style={{ borderColor: "#007bff", borderWidth: "2px" }}
                      >
                        <div className="card-body py-3">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div>
                              <div className="small text-secondary">รหัสสินค้า</div>
                              <div className="fw-semibold">{entry.product.code}</div>
                            </div>
                            <div className="text-end">
                              <div className="small text-secondary">ราคา</div>
                              {editingPriceIdx === idx ? (
                                <input
                                  type="number"
                                  className="form-control form-control-sm text-end"
                                  style={{ width: 100 }}
                                  min={0}
                                  value={draftPrice}
                                  autoFocus
                                  onChange={(e) => setDraftPrice(e.target.value)}
                                  onBlur={() => confirmPrice(idx)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") confirmPrice(idx);
                                    if (e.key === "Escape") setEditingPriceIdx(null);
                                  }}
                                />
                              ) : (
                                <div className="fs-5 fw-bold text-primary d-flex align-items-center gap-1 justify-content-end">
                                  ฿{getEffectivePrice(entry).toFixed(2)}
                                  <button
                                    type="button"
                                    className="btn btn-link btn-sm p-0 text-secondary"
                                    title="แก้ไขราคา"
                                    onClick={() => { setEditingPriceIdx(idx); setDraftPrice(String(getEffectivePrice(entry))); }}
                                  >✏️</button>
                                  {entry.customPrice !== undefined && (
                                    <button
                                      type="button"
                                      className="btn btn-link btn-sm p-0 text-danger"
                                      title={`ย้อนกลับราคาเดิม ฿${entry.product.wholesalePrice.toFixed(2)}`}
                                      onClick={() => setItems((prev) => {
                                        const copy = [...prev];
                                        const { customPrice: _, ...rest } = copy[idx];
                                        copy[idx] = rest as ItemEntry;
                                        return copy;
                                      })}
                                    >↩</button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="fw-semibold text-dark">{entry.product.name}</div>
                          <div className="row g-2 align-items-center mt-2">
                            <div className="col-auto small text-secondary">จำนวน</div>
                            <div className="col-4 col-sm-3 col-md-3">
                              <input
                                type="number"
                                min={0}
                                step={1}
                                className="form-control"
                                value={entry.qty}
                                onChange={(e) => setQty(idx, Number(e.target.value))}
                              />
                            </div>
                            {entry.product.unit && (
                              <div className="col-auto small text-muted">หน่วย: {entry.product.unit}</div>
                            )}
                          </div>
                          <div className="border border-primary rounded-3 bg-white shadow-sm mt-3 px-3 py-2">
                            <div className="d-flex justify-content-between align-items-center">
                              <div className="small text-black">
                                รวมราคา <span className="fw-semibold text-dark">“{entry.product.name}”</span>
                              </div>
                              <div className="fs-4 fw-bold">
                                {(getEffectivePrice(entry) * entry.qty).toLocaleString(undefined, {
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                                })}
                              </div>
                            </div>
                          </div>

                          <button
                            type="button"
                            className="btn btn-outline-danger w-100 mt-2"
                            onClick={() => removeItem(idx)}
                          >
                            ลบรายการนี้
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {errorMsg && (
                  <div className="alert alert-warning rounded-3" role="alert">
                    <div className="fw-semibold">ข้อผิดพลาด:</div>
                    <div className="text-break">{errorMsg}</div>
                  </div>
                )}

                {/* กรอกมือสำรอง */}
                <div className="mt-3">
                  <button
                    type="button"
                    className="btn btn-outline-primary w-100"
                    aria-expanded={showManual}
                    onClick={() => setShowManual((s) => !s)}
                  >
                    {showManual ? "ซ่อนการพิมพ์รหัสด้วยตนเอง" : "พิมพ์รหัสด้วยตนเอง (ตัวเลือก)"}
                  </button>

                  {showManual && (
                    <div className="border rounded-3 p-3 mt-2 bg-white">
                      <form
                        className="row gy-3"
                        onSubmit={async (e) => {
                          e.preventDefault();
                          const form = e.currentTarget as HTMLFormElement;
                          const input = form.querySelector<HTMLInputElement>("#manualCode");
                          const code = input?.value.trim() ?? "";
                          if (!code) return;
                          setResultText(code);
                          playBeep();
                          const p = await fetchProduct(code);
                          if (p) {
                            let toastName = p.name;
                            let toastQty = 1;
                            setItems((prev) => {
                              const idx = prev.findIndex((it) => it.product.code === code);
                              if (idx >= 0) {
                                const copy = [...prev];
                                const newQty = copy[idx].qty + 1;
                                copy[idx] = { ...copy[idx], qty: newQty };
                                toastName = copy[idx].product.name;
                                toastQty = newQty;
                                return copy;
                              }
                              toastName = p.name;
                              toastQty = 1;
                              return [{ product: p, qty: 1 }, ...prev];
                            });
                            setToast({ show: true, message: `เพิ่ม ${toastName} x${toastQty}` });
                          }
                          if (input) input.value = "";
                        }}
                      >
                        <div className="col-12">
                          <label htmlFor="manualCode" className="form-label">
                            รหัส (พิมพ์แทนการสแกน)
                          </label>
                          <input
                            id="manualCode"
                            type="text"
                            className="form-control"
                            placeholder="dc025 / 885... / ข้อความ"
                          />
                        </div>
                        <div className="col-12 d-flex gap-2">
                          <button type="submit" className="btn btn-primary flex-fill">
                            เพิ่มรายการ
                          </button>
                          <button type="button" className="btn btn-outline-secondary flex-fill" onClick={stopScan}>
                            หยุดกล้อง
                          </button>
                          <button type="button" className="btn btn-outline-primary flex-fill" onClick={clearItems}>
                            ล้างรายการ
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                </div>

                <div className="d-grid gap-2 mb-3">

                  {/* ✅ ทางเลือกใหม่: ถ่าย/อัปโหลดรูปแล้วถอดรหัส */}
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    📷 ถ่าย/อัปโหลดรูป QR
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"   // มือถือจะเปิดกล้องหลังเป็นตัวเลือก
                    className="d-none"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) decodeFromImageFile(f);
                      e.currentTarget.value = ""; // รีเซ็ตให้เลือกไฟล์ซ้ำได้
                    }}
                  />

                </div>

                {/* การ์ดยอดรวมทั้งบิล */}
                <div className="card border-secondary bg-primary text-light shadow-sm mt-4" style={{ borderWidth: "8px" }}>
                  <div className="card-body">
                    <div className="fw-semibold small mb-2">💰 ยอดรวมบิลทั้งหมด</div>

                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="badge bg-light text-primary">
                        จำนวนชิ้นรวม: <span className="fw-bold">{totalQty}</span>
                      </span>
                      <div className="text-end">
                        <div className="small">ยอดก่อนหักส่วนลด</div>
                        <div className="fs-5 fw-bold">{grandTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })} บาท</div>
                      </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center">
                    
                      <div className="small">ส่วนลด</div>
                      <div className="d-flex align-items-center gap-2">
                        <div className="fs-5">- {discount.toLocaleString(undefined, { maximumFractionDigits: 0 })} บาท</div>
                        {/* ปุ่มเปิด/ปิดแถบส่วนลด (สไตล์เหมือน “พิมพ์รหัสด้วยตนเอง”) */}
                        <button
                          type="button"
                          className="btn btn-outline-light btn-sm"
                          aria-expanded={showDiscountPanel}
                          onClick={() => {
                            setDraftDiscount(discount); // sync ค่าเดิมเข้า draft
                            setDiscountErr("");
                            setShowDiscountPanel((s) => !s);
                          }}
                        >
                          {showDiscountPanel ? "ซ่อนส่วนลด" : "ตั้งค่าส่วนลด (ตัวเลือก)"}
                        </button>
                      </div>
                    </div>

                    {/* แผงส่วนลดแบบพับลง */}
                    {showDiscountPanel && (
                      <div className="border rounded-3 p-3 mt-2 bg-white">
                        <div className="mb-2 small text-muted">
                          ยอดก่อนหักส่วนลด:{" "}
                          <b>{grandTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })} บาท</b>
                        </div>

                        <label className="form-label">จำนวนเงินส่วนลด (บาท)</label>
                        <div className="input-group mb-2">
                          <span className="input-group-text">฿</span>
                          <input
                            type="number"
                            className="form-control"
                            min={0}
                            step={1}
                            value={Number.isFinite(draftDiscount) ? draftDiscount : 0}
                            onChange={(e) => {
                              const v = Math.floor(Number(e.target.value) || 0);
                              setDraftDiscount(v);
                              if (v < 0) setDiscountErr("ส่วนลดต้องเป็นจำนวนเต็มไม่ติดลบ");
                              else if (v > grandTotal) setDiscountErr("ส่วนลดมากกว่ายอดก่อนหักส่วนลดไม่ได้");
                              else setDiscountErr("");
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                const v = Math.max(0, Math.min(grandTotal, Math.floor(draftDiscount || 0)));
                                if (!discountErr) setDiscount(v);
                              }
                            }}
                          />

                          {/* ปุ่มเพิ่มส่วนลด */}
                          {[ 20, 50, 100].map((amt) => (
                            <button
                              key={`plus${amt}`}
                              className="btn btn-outline-secondary"
                              type="button"
                              onClick={() => {
                                const v = Math.min(grandTotal, Math.max(0, draftDiscount + amt));
                                setDraftDiscount(v);
                                setDiscountErr(v > grandTotal ? "ส่วนลดมากกว่ายอดก่อนหักส่วนลดไม่ได้" : "");
                              }}
                              title={`-${amt} บาท`}
                            >
                              -{amt}
                            </button>
                          ))}

                        

                          {/* ปุ่มเคลียร์ */}
                          <button
                            className="btn btn-outline-secondary"
                            type="button"
                            onClick={() => {
                              setDraftDiscount(0);
                              setDiscountErr("");
                            }}
                          >
                            เคลียร์
                          </button>
                        </div>


                        {/* ปุ่มเปอร์เซ็นต์ช่วยคิดเร็ว */}
                        <div className="d-flex flex-wrap gap-2 mb-2">
                          {[5, 10, 15, 20].map((pct) => (
                            <button
                              key={pct}
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => {
                                const v = Math.floor((grandTotal * pct) / 100);
                                setDraftDiscount(v);
                                setDiscountErr(v > grandTotal ? "ส่วนลดมากกว่ายอดก่อนหักส่วนลดไม่ได้" : "");
                              }}
                            >
                              {pct}% ≈ {Math.floor((grandTotal * pct) / 100).toLocaleString()}฿
                            </button>
                          ))}
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => {
                              const v = grandTotal;
                              setDraftDiscount(v);
                              setDiscountErr("");
                            }}
                          >
                            เททั้งยอด
                          </button>
                        </div>

                        {discountErr && (
                          <div className="alert alert-warning py-2 small">{discountErr}</div>
                        )}

                        <div className="border rounded-3 p-3 bg-light">
                          <div className="d-flex justify-content-between">
                            <div>สุทธิหลังหักส่วนลด</div>
                            <div className="fw-bold">
                              {Math.max(0, grandTotal - (draftDiscount || 0)).toLocaleString(undefined, {
                                maximumFractionDigits: 0,
                              })}{" "}
                              บาท
                            </div>
                          </div>
                        </div>

                        <div className="d-flex gap-2 mt-3">
                          <button
                            type="button"
                            className="btn btn-primary flex-fill"
                            disabled={!!discountErr || draftDiscount < 0 || draftDiscount > grandTotal}
                            onClick={() => {
                              const v = Math.max(0, Math.min(grandTotal, Math.floor(draftDiscount || 0)));
                              setDiscount(v);
                              setShowDiscountPanel(false); // ยุบแผงหลังยืนยัน
                            }}
                          >
                            ใช้ส่วนลดนี้
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-secondary flex-fill"
                            onClick={() => setShowDiscountPanel(false)}
                          >
                            ปิดแถบ
                          </button>
                        </div>
                      </div>
                    )}
                   
                 
                    <hr className="border-light" />
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="fw-semibold">ยอดสุทธิ</div>
                      <div className="fs-3 fw-bold">{netTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })} บาท</div>
                    </div>
                  </div>
                </div>

                {/* ปุ่มบันทึกข้อมูลการขาย */}
                <div className="d-grid mt-3">
                  <button
                    type="button"
                    className="btn btn-primary btn-lg"
                    disabled={items.length === 0 || saving}
                    onClick={async () => {
                      // PMN-CRITICAL(/ScanWholesalePage): bill number, wholesale saleType, flattened Thai payload keys, and /sales_save are a coupled backend contract.
                      // ✅ เพิ่ม: บังคับให้เลือกวิธีชำระเงินก่อนบันทึก
                      if (!paymentMethod) {
                        setToast({ show: true, message: "กรุณาเลือกวิธีชำระเงินก่อนบันทึก" });
                        setShowPaymentModal(true); // เปิดหน้าต่างให้เลือกทันที
                        return; // ตัดจบ ไม่เข้าโค้ดบันทึก
                      }

                      if (saving) return;
                      setSaving(true);

                      try {
                        let billNo = "000";
                        const r = await fetch(apiUrl("/orders/prenext_bill_no"));
                        const j = await r.json().catch(() => ({}));
                        if (!r.ok) throw new Error(j?.error ?? r.statusText);

                        const last = j?.next;
                        const pad = (n: number, width = 3) => String(n).padStart(width, "0");
                        billNo = last == null ? pad(0) : String(last);

                        const nextBillNo = String((parseInt(billNo, 10) || 0) + 1).padStart(billNo.length || 3, "0");

                        const now = new Date();
                        const toBEThaiDate = (d: Date) => {
                          const tz = "Asia/Bangkok";
                          const parts = new Intl.DateTimeFormat("en-GB", {
                            timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
                          }).formatToParts(d);
                          const day = parts.find((p) => p.type === "day")?.value!;
                          const month = parts.find((p) => p.type === "month")?.value!;
                          const year = parts.find((p) => p.type === "year")?.value!;
                          const beYear = String(Number(year) + 543);
                          return `${day}/${month}/${beYear}`;
                        };
                        const toThaiTime = (d: Date) =>
                          new Intl.DateTimeFormat("en-GB", {
                            timeZone: "Asia/Bangkok", hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit",
                          }).format(d);

                        const thaiDate = toBEThaiDate(now);
                        const thaiTime = toThaiTime(now);

                        const salesFlat = items.flatMap((it) =>
                          Array.from({ length: it.qty }, () => ({
                            "รหัสสินค้า": it.product.code,
                            "ชื่อสินค้า": it.product.name,
                            "จำนวน": 1,
                            "ราคา": getEffectivePrice(it),
                          }))
                        );
                        const salesNumbered = salesFlat.map((row, i) => ({ "ลำดับ": i + 1, ...row }));

                        const payload = {
                          paymentMethod, // ตอนนี้มีค่าแน่นอนแล้ว
                          saleType: "wholesale" as const, // ถ้าอยากแยก mall จริง ๆ เดี๋ยวค่อยไปปรับฝั่ง server เป็น "mall"
                          "เลขบิล": nextBillNo,
                          "วันที่บันทึก": thaiDate,
                          "เวลาบันทึก": thaiTime,
                          ...Object.assign({}, ...salesNumbered.map((r, i) => ({ [i]: r }))),
                          "ราคาทั้งบิล": grandTotal,
                          "จำนวนชิ้นรวม": totalQty,
                          "ส่วนลด": discount,
                          "ยอดสุทธิ": netTotal,
                        };

                        const resp1 = await fetch(apiUrl("/sales_save"), {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify(payload),
                        });
                        const data1 = await resp1.json().catch(() => ({}));
                        if (!resp1.ok) {
                          console.error("❌ Sales save failed:", data1);
                          alert(`บันทึกบิลไม่สำเร็จ: ${data1?.error ?? resp1.statusText}`);
                          setSaving(false);
                          return;
                        }

                        // PMN-NOTE(/ScanWholesalePage): discount is saved after the main bill; failure must be handled as a separate side effect.
                        // เซฟส่วนลด
                        try {
                          const resp2 = await fetch(apiUrl(`/sales/discounts/${encodeURIComponent(nextBillNo)}`), {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ discount_amount: discount, total_amount: netTotal }),
                          });
                          const data2 = await resp2.json().catch(() => ({}));
                          if (!resp2.ok) {
                            console.error("❌ Save discount failed:", data2);
                            alert(`บันทึกส่วนลดไม่สำเร็จ: ${data2?.error ?? resp2.statusText}`);
                          }
                        } catch (err) {
                          console.error("❌ API error (discount):", err);
                          alert("เชื่อมต่อ API ส่วนลดไม่ได้ — ตรวจสอบเซิร์ฟเวอร์/เน็ตเวิร์ค");
                        }

                        alert(`บันทึกข้อมูลการขายเรียบร้อย! เลขบิล: ${nextBillNo}`);
                        resetUIAfterSave(nextBillNo);
                      } catch (e: any) {
                        console.error("❌ Submit error:", e);
                        alert(e?.message ?? "เกิดข้อผิดพลาดระหว่างบันทึก");
                      } finally {
                        setSaving(false);
                      }
                    }}
                  >
                    {saving ? "กำลังบันทึก…" : "💾 บันทึกข้อมูลการขาย"}
                  </button>
                </div>


                <div className="text-center mt-4">
                  <a href="/" className="btn btn-link text-decoration-none">
                    ← กลับหน้าเมนู
                  </a>
                </div>
              </div>
            </div>

            <div className="text-center small text-muted mt-3">
              * ต้องเปิดผ่าน <code>https</code> หรือ <code>http://localhost</code> เพื่อให้กล้องทำงาน โดยเฉพาะบนมือถือ
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
